import { useState } from 'react';
import { uniq } from 'lodash';
import { assignMarketsToSelectedCreatives, getAllCompatibleMarkets } from 'modules/api/creativesAdministration';
import { cancelFunctions } from 'utils/cancelFunctions';
import { getCommonMarkets } from 'utils/commonMarkets';
import { Creative } from 'components/common/types/Creative.types';
import { SelectedMarket, UseCreativeAdministration } from './CreativeAdministration.types';

const useCreativeAdministration = (): UseCreativeAdministration => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [areMarketsUpdated, setAreMarketsUpdated] = useState(false);
  const [selectedCreatives, setSelectedCreatives] = useState<Creative[]>([]);
  const [selectedMarkets, setSelectedMarkets] = useState<SelectedMarket[]>([]);
  const [commonMarkets, setCommonMarkets] = useState<string[]>([]);

  const handleOpenDialog = async (): Promise<void> => {
    try {
      const dspsCodes = uniq(selectedCreatives.map((creative) => creative.dsp.code));
      const allCompatibleMarkets = await getAllCompatibleMarkets(cancelFunctions, dspsCodes);
      const fetchedCommonMarkets = getCommonMarkets(selectedCreatives);
      const selectedCompatibleMarkets = allCompatibleMarkets.map((market: string) => ({
        market,
        isSelected: fetchedCommonMarkets.includes(market),
      }));

      setIsDialogOpen(true);
      setAreMarketsUpdated(false);
      setSelectedMarkets(selectedCompatibleMarkets);
      setCommonMarkets(fetchedCommonMarkets);
    } catch {} // eslint-disable-line no-empty
  };

  const handleCloseDialog = (): void => {
    setIsDialogOpen(false);
  };

  const handleSelectMarket = (value: string | number, isChecked: boolean): void => {
    const updatedSelectedMarkets = selectedMarkets.map(({ market, isSelected }) =>
      value === market
        ? {
            market,
            isSelected: isChecked,
          }
        : { market, isSelected },
    );
    setSelectedMarkets(updatedSelectedMarkets);
  };

  const handleConfirm = async (): Promise<void> => {
    try {
      const areSavedMarketsUpdated = await assignMarketsToSelectedCreatives(
        cancelFunctions,
        selectedCreatives,
        selectedMarkets,
        commonMarkets,
      );

      setAreMarketsUpdated(areSavedMarketsUpdated);
      setIsDialogOpen(false);
      setSelectedCreatives([]);
    } catch (error) {
      setAreMarketsUpdated(false);
      setIsDialogOpen(false);
    }
  };

  return {
    isDialogOpen,
    selectedCreatives,
    selectedMarkets,
    areMarketsUpdated,
    handleOpenDialog,
    handleCloseDialog,
    handleSelectMarket,
    handleConfirm,
    setSelectedCreatives,
  };
};

export default useCreativeAdministration;
